import React, { useState } from 'react';
import {
  Card,
  Button,
  Upload,
  Typography,
  Steps,
  Form,
  Input,
  Select,
  InputNumber,
  Space,
  Tag,
  Spin,
  Alert,
  Divider,
  message,
  Descriptions,
  Row,
  Col,
  Collapse,
  Table,
  List,
  Tabs,
  Tooltip,
  Checkbox,
  Modal,
} from 'antd';
import {
  MailOutlined,
  FileExcelOutlined,
  RobotOutlined,
  EditOutlined,
  SaveOutlined,
  ReloadOutlined,
  FileZipOutlined,
  TableOutlined,
  EyeOutlined,
  QuestionCircleOutlined,
  PlusOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { API } from '@/services/api.service';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { Dragger } = Upload;

// Column mapping field definitions
const COLUMN_MAPPING_FIELDS = [
  { key: 'regularHours', label: 'Regular Hours', description: 'Redovni sati' },
  { key: 'overtime', label: 'Overtime', description: 'Prekovremeni' },
  { key: 'weekendOvertime', label: 'Weekend Overtime', description: 'Vikend prekovremeni' },
  { key: 'nightShift', label: 'Night Shift', description: 'Noćna smena' },
  { key: 'publicHolidayHours', label: 'Public Holiday Hours', description: 'Praznik sati' },
  { key: 'annualLeave', label: 'Annual Leave', description: 'Godišnji odmor' },
  { key: 'sickLeave', label: 'Sick Leave', description: 'Bolovanje' },
  { key: 'paidLeave', label: 'Paid Leave', description: 'Plaćeno odsustvo' },
  { key: 'unpaidLeave', label: 'Unpaid Leave', description: 'Neplaćeno odsustvo' },
  { key: 'totalHours', label: 'Total Hours', description: 'Ukupno sati' },
];

// Generate column options A-Z, AA-AZ
const generateColumnOptions = () => {
  const options = [];
  // A-Z
  for (let i = 0; i < 26; i++) {
    options.push(String.fromCharCode(65 + i));
  }
  // AA-AZ
  for (let i = 0; i < 26; i++) {
    options.push('A' + String.fromCharCode(65 + i));
  }
  return options;
};

const COLUMN_OPTIONS = generateColumnOptions();

export default function FormatAnalyzerPanel({ onConfigCreated }) {
  const { t } = useTranslation(['tables', 'forms', 'messages']);
  const [currentStep, setCurrentStep] = useState(0);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [emlPreview, setEmlPreview] = useState(null);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [editedConfig, setEditedConfig] = useState(null);
  const [selectedPreviewSheet, setSelectedPreviewSheet] = useState(null);
  const [multipleFormats, setMultipleFormats] = useState(false);
  const [formatMappings, setFormatMappings] = useState([]);
  const [editingFormatIndex, setEditingFormatIndex] = useState(null);
  const [formatModalVisible, setFormatModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [formatForm] = Form.useForm();

  const sp = (key) => t(`smartParser.${key}`, { ns: 'tables', defaultValue: key });

  // Fetch companies for dropdown
  const { data: companiesData } = useQuery({
    queryKey: ['hlb-companies-dropdown'],
    queryFn: () => API.companies.retrieveDropdownAllCompanies(),
  });

  const companies = companiesData?.items || [];

  // Preview EML mutation
  const previewMutation = useMutation({
    mutationFn: (formData) => API.smartParser.previewEML(formData),
    onSuccess: (response) => {
      const preview = response.data;
      setEmlPreview(preview);
      message.success(`Found ${preview.attachmentCount} Excel attachment(s)`);
    },
    onError: (error) => {
      message.error(error.response?.data?.message || 'Failed to preview EML');
    },
  });

  // Analyze EML mutation
  const analyzeMutation = useMutation({
    mutationFn: (formData) => API.smartParser.analyzeEML(formData),
    onSuccess: (response) => {
      const result = response.data;
      setAnalysisResult(result);
      setEditedConfig(result.suggestedConfig);

      // Pre-fill form with suggested values
      form.setFieldsValue({
        name: result.suggestedConfig?.name || `Config for ${uploadedFile?.name}`,
        description: result.suggestedConfig?.description || '',
        companyId: result.suggestedConfig?.companyId,
      });
      setCurrentStep(2);
      message.success('EML analyzed successfully');
    },
    onError: (error) => {
      message.error(error.response?.data?.message || 'Failed to analyze EML');
    },
  });

  // Create config mutation
  const createConfigMutation = useMutation({
    mutationFn: (data) => API.smartParser.createConfig(data),
    onSuccess: () => {
      message.success('Configuration created successfully');
      handleReset();
      onConfigCreated?.();
    },
    onError: (error) => {
      message.error(error.response?.data?.message || 'Failed to create configuration');
    },
  });

  const handleFileUpload = (info) => {
    const { file } = info;
    if (file.status === 'removed') {
      setUploadedFile(null);
      setEmlPreview(null);
      return;
    }
    setUploadedFile(file.originFileObj || file);
  };

  const handlePreview = async () => {
    if (!uploadedFile) {
      message.warning('Please upload an EML file first');
      return;
    }

    const formData = new FormData();
    formData.append('file', uploadedFile);

    previewMutation.mutate(formData);
  };

  const handleAnalyze = async () => {
    const configName = form.getFieldValue('configName');
    const companyId = form.getFieldValue('companyId');

    if (!configName) {
      message.warning('Please enter a configuration name');
      return;
    }

    const formData = new FormData();
    formData.append('file', uploadedFile);
    formData.append('configName', configName);
    if (companyId) {
      formData.append('companyId', companyId);
    }

    analyzeMutation.mutate(formData);
  };

  const handleSaveConfig = async (values) => {
    const configData = {
      ...editedConfig,
      name: values.name,
      description: values.description,
      companyId: values.companyId || null,
      multipleFormats,
      formatMappings: multipleFormats ? formatMappings : [],
    };

    createConfigMutation.mutate(configData);
  };

  const handleReset = () => {
    setCurrentStep(0);
    setUploadedFile(null);
    setEmlPreview(null);
    setAnalysisResult(null);
    setEditedConfig(null);
    setSelectedPreviewSheet(null);
    setMultipleFormats(false);
    setFormatMappings([]);
    setEditingFormatIndex(null);
    form.resetFields();
  };

  // Handle multiple formats toggle
  const handleMultipleFormatsChange = (e) => {
    const enabled = e.target.checked;
    setMultipleFormats(enabled);

    if (enabled && formatMappings.length === 0) {
      // Initialize with the current config as default format
      const defaultFormat = {
        id: 'default',
        name: 'Default Format',
        detection: {
          type: 'filename_pattern',
          patterns: [],
          caseSensitive: false,
        },
        excelConfig: editedConfig?.excelConfig || {},
        columnMapping: editedConfig?.columnMapping || {},
        textMarkerConfig: editedConfig?.textMarkerConfig || {},
        isDefault: true,
      };
      setFormatMappings([defaultFormat]);
    }
  };

  // Add new format mapping
  const handleAddFormatMapping = () => {
    setEditingFormatIndex(null);
    formatForm.resetFields();
    formatForm.setFieldsValue({
      name: '',
      patterns: '',
      isDefault: false,
    });
    setFormatModalVisible(true);
  };

  // Edit existing format mapping
  const handleEditFormatMapping = (index) => {
    const format = formatMappings[index];
    setEditingFormatIndex(index);
    formatForm.setFieldsValue({
      name: format.name,
      patterns: format.detection?.patterns?.join(', ') || '',
      isDefault: format.isDefault || false,
    });
    setFormatModalVisible(true);
  };

  // Delete format mapping
  const handleDeleteFormatMapping = (index) => {
    const newMappings = [...formatMappings];
    newMappings.splice(index, 1);
    setFormatMappings(newMappings);
  };

  // Save format mapping from modal
  const handleSaveFormatMapping = (values) => {
    const formatConfig = {
      id: editingFormatIndex !== null
        ? formatMappings[editingFormatIndex].id
        : `format_${Date.now()}`,
      name: values.name,
      detection: {
        type: 'filename_pattern',
        patterns: values.patterns
          ? values.patterns.split(',').map((p) => p.trim().toLowerCase()).filter(Boolean)
          : [],
        caseSensitive: false,
      },
      // Copy current main config as starting point for new format
      excelConfig: editingFormatIndex !== null
        ? formatMappings[editingFormatIndex].excelConfig
        : { ...editedConfig?.excelConfig },
      columnMapping: editingFormatIndex !== null
        ? formatMappings[editingFormatIndex].columnMapping
        : { ...editedConfig?.columnMapping },
      textMarkerConfig: editingFormatIndex !== null
        ? formatMappings[editingFormatIndex].textMarkerConfig
        : { ...editedConfig?.textMarkerConfig },
      isDefault: values.isDefault || false,
    };

    const newMappings = [...formatMappings];
    if (editingFormatIndex !== null) {
      newMappings[editingFormatIndex] = formatConfig;
    } else {
      newMappings.push(formatConfig);
    }

    // If this is marked as default, unmark others
    if (values.isDefault) {
      newMappings.forEach((m, i) => {
        if (i !== (editingFormatIndex ?? newMappings.length - 1)) {
          m.isDefault = false;
        }
      });
    }

    setFormatMappings(newMappings);
    setFormatModalVisible(false);
    formatForm.resetFields();
  };

  // Update column mapping for a specific format
  const handleFormatColumnMappingChange = (formatIndex, field, columnLetter) => {
    const newMappings = [...formatMappings];
    if (!newMappings[formatIndex].columnMapping) {
      newMappings[formatIndex].columnMapping = {};
    }
    newMappings[formatIndex].columnMapping[field] = {
      ...newMappings[formatIndex].columnMapping[field],
      columnLetter: columnLetter || null,
    };
    setFormatMappings(newMappings);
  };

  // Update excel config for a specific format
  const handleFormatExcelConfigChange = (formatIndex, path, value) => {
    const newMappings = [...formatMappings];
    if (!newMappings[formatIndex].excelConfig) {
      newMappings[formatIndex].excelConfig = {};
    }

    const pathParts = path.split('.');
    let current = newMappings[formatIndex].excelConfig;

    for (let i = 0; i < pathParts.length - 1; i++) {
      if (!current[pathParts[i]]) {
        current[pathParts[i]] = {};
      }
      current = current[pathParts[i]];
    }

    current[pathParts[pathParts.length - 1]] = value;
    setFormatMappings(newMappings);
  };

  // Update skip sheets for a specific format
  const handleFormatSkipSheetsChange = (formatIndex, value) => {
    const newMappings = [...formatMappings];
    if (!newMappings[formatIndex].excelConfig) {
      newMappings[formatIndex].excelConfig = {};
    }
    // Parse comma-separated string to array
    const sheets = value ? value.split(',').map((s) => s.trim().toLowerCase()).filter(Boolean) : [];
    newMappings[formatIndex].excelConfig.skipSheets = sheets;
    setFormatMappings(newMappings);
  };

  // Update column mapping in editedConfig
  const handleColumnMappingChange = (field, columnLetter) => {
    setEditedConfig((prev) => ({
      ...prev,
      columnMapping: {
        ...prev.columnMapping,
        [field]: {
          ...prev.columnMapping?.[field],
          columnLetter: columnLetter || null,
        },
      },
    }));
  };

  // Update excel config settings
  const handleExcelConfigChange = (path, value) => {
    setEditedConfig((prev) => {
      const newConfig = { ...prev };
      const pathParts = path.split('.');
      let current = newConfig;

      for (let i = 0; i < pathParts.length - 1; i++) {
        if (!current[pathParts[i]]) {
          current[pathParts[i]] = {};
        }
        current = current[pathParts[i]];
      }

      current[pathParts[pathParts.length - 1]] = value;
      return newConfig;
    });
  };

  const steps = [
    {
      title: 'Upload EML',
      icon: <MailOutlined />,
    },
    {
      title: 'Preview & Analyze',
      icon: <RobotOutlined />,
    },
    {
      title: 'Review & Save',
      icon: <SaveOutlined />,
    },
  ];

  // Render Excel Preview Table
  const renderExcelPreviewTable = (attachment) => {
    if (!attachment?.sheets || attachment.sheets.length === 0) {
      return <Text type="secondary">No sheet data available</Text>;
    }

    // Find full sheet data from emlPreview
    const fullAttachment = emlPreview?.attachments?.find(
      (a) => a.filename === attachment.filename
    );

    // Get sample rows from the structure
    const sheetData = fullAttachment?.structure?.sheets?.[0];
    const sampleRows = sheetData?.sampleRows || [];

    if (sampleRows.length === 0) {
      return <Text type="secondary">No preview data available for this file</Text>;
    }

    // Build columns dynamically from the first row
    const maxColumns = Math.max(...sampleRows.map((row) => row.cells?.length || 0), 0);
    const columns = [
      {
        title: 'Row',
        dataIndex: 'rowNumber',
        key: 'rowNumber',
        width: 50,
        fixed: 'left',
        render: (val) => <Text type="secondary">{val}</Text>,
      },
      ...Array.from({ length: Math.min(maxColumns, 15) }, (_, i) => ({
        title: COLUMN_OPTIONS[i] || `Col ${i + 1}`,
        dataIndex: `col_${i}`,
        key: `col_${i}`,
        width: 100,
        ellipsis: true,
        render: (val, record) => {
          const cell = record.cells?.find((c) => c.column === i + 1);
          const value = cell?.value;

          if (value === null || value === undefined || value === '') {
            return <Text type="secondary">-</Text>;
          }

          // Highlight if this is the detected header row
          const headerRow = editedConfig?.excelConfig?.headerDetection?.fixedRow;
          const isHeaderRow = record.rowNumber === headerRow;

          return (
            <Tooltip title={String(value)}>
              <Text
                style={{
                  fontWeight: isHeaderRow ? 'bold' : 'normal',
                  color: isHeaderRow ? '#1890ff' : undefined,
                }}
              >
                {String(value).substring(0, 20)}
                {String(value).length > 20 ? '...' : ''}
              </Text>
            </Tooltip>
          );
        },
      })),
    ];

    // Transform rows for table
    const dataSource = sampleRows.map((row) => ({
      key: row.rowNumber,
      rowNumber: row.rowNumber,
      cells: row.cells,
    }));

    return (
      <div>
        <div style={{ marginBottom: 8 }}>
          <Space>
            <Text strong>{attachment.filename}</Text>
            <Tag>{attachment.format?.toUpperCase()}</Tag>
            <Text type="secondary">{attachment.sheetCount} sheet(s)</Text>
          </Space>
        </div>
        <Table
          columns={columns}
          dataSource={dataSource}
          size="small"
          pagination={false}
          scroll={{ x: 'max-content' }}
          bordered
          style={{ fontSize: 12 }}
        />
        <div style={{ marginTop: 8 }}>
          <Text type="secondary" style={{ fontSize: 11 }}>
            Showing first {sampleRows.length} rows. Blue highlighted row is the detected header row.
          </Text>
        </div>
      </div>
    );
  };

  // Render Column Mapping Editor
  const renderColumnMappingEditor = () => {
    const columnMapping = editedConfig?.columnMapping || {};

    return (
      <div>
        <Alert
          message="Edit Column Mappings"
          description="Adjust which Excel column corresponds to each field. Leave empty if the field is not present in the Excel."
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <Row gutter={[16, 12]}>
          {COLUMN_MAPPING_FIELDS.map((field) => (
            <Col span={8} key={field.key}>
              <div style={{ marginBottom: 4 }}>
                <Text strong style={{ fontSize: 12 }}>{field.label}</Text>
                <Text type="secondary" style={{ fontSize: 11, marginLeft: 8 }}>
                  ({field.description})
                </Text>
              </div>
              <Select
                placeholder="Select column"
                value={columnMapping[field.key]?.columnLetter || undefined}
                onChange={(value) => handleColumnMappingChange(field.key, value)}
                allowClear
                style={{ width: '100%' }}
                size="small"
              >
                {COLUMN_OPTIONS.map((col) => (
                  <Option key={col} value={col}>
                    Column {col}
                  </Option>
                ))}
              </Select>
            </Col>
          ))}
        </Row>
      </div>
    );
  };

  // Render Structure Settings Editor
  const renderStructureEditor = () => {
    const excelConfig = editedConfig?.excelConfig || {};
    const headerRow = excelConfig.headerDetection?.fixedRow || 5;
    const dataRowCount = excelConfig.dataRows?.fixedCount || 31;
    const dataStartOffset = excelConfig.dataRows?.startOffset || 1;

    return (
      <div>
        <Alert
          message="Edit Excel Structure Settings"
          description="Adjust the row positions if AI detected them incorrectly."
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <Row gutter={24}>
          <Col span={8}>
            <div style={{ marginBottom: 4 }}>
              <Text strong>Header Row</Text>
              <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>
                Row containing column headers
              </Text>
            </div>
            <InputNumber
              min={1}
              max={50}
              value={headerRow}
              onChange={(value) => handleExcelConfigChange('excelConfig.headerDetection.fixedRow', value)}
              style={{ width: '100%' }}
            />
          </Col>
          <Col span={8}>
            <div style={{ marginBottom: 4 }}>
              <Text strong>Data Start Offset</Text>
              <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>
                Rows after header where data starts
              </Text>
            </div>
            <InputNumber
              min={1}
              max={10}
              value={dataStartOffset}
              onChange={(value) => handleExcelConfigChange('excelConfig.dataRows.startOffset', value)}
              style={{ width: '100%' }}
            />
          </Col>
          <Col span={8}>
            <div style={{ marginBottom: 4 }}>
              <Text strong>Data Row Count</Text>
              <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>
                Number of data rows (days in month)
              </Text>
            </div>
            <InputNumber
              min={1}
              max={50}
              value={dataRowCount}
              onChange={(value) => handleExcelConfigChange('excelConfig.dataRows.fixedCount', value)}
              style={{ width: '100%' }}
            />
          </Col>
        </Row>

        <Divider style={{ margin: '16px 0' }} />

        <Row gutter={24}>
          <Col span={12}>
            <div style={{ marginBottom: 4 }}>
              <Text strong>Employee Name Location</Text>
            </div>
            <Space>
              <Text type="secondary">Row:</Text>
              <InputNumber
                min={1}
                max={20}
                value={excelConfig.employeeNameSource?.fixedCell?.row || 2}
                onChange={(value) => handleExcelConfigChange('excelConfig.employeeNameSource.fixedCell.row', value)}
                style={{ width: 80 }}
                size="small"
              />
              <Text type="secondary">Column:</Text>
              <Select
                value={excelConfig.employeeNameSource?.fixedCell?.column || 'B'}
                onChange={(value) => handleExcelConfigChange('excelConfig.employeeNameSource.fixedCell.column', value)}
                style={{ width: 80 }}
                size="small"
              >
                {COLUMN_OPTIONS.slice(0, 10).map((col) => (
                  <Option key={col} value={col}>{col}</Option>
                ))}
              </Select>
            </Space>
          </Col>
          <Col span={12}>
            <div style={{ marginBottom: 4 }}>
              <Text strong>JMBG Location</Text>
            </div>
            <Space>
              <Text type="secondary">Row:</Text>
              <InputNumber
                min={1}
                max={20}
                value={excelConfig.jmbgExtraction?.location?.fixedCell?.row || 3}
                onChange={(value) => handleExcelConfigChange('excelConfig.jmbgExtraction.location.fixedCell.row', value)}
                style={{ width: 80 }}
                size="small"
              />
              <Text type="secondary">Column:</Text>
              <Select
                value={excelConfig.jmbgExtraction?.location?.fixedCell?.column || 'B'}
                onChange={(value) => handleExcelConfigChange('excelConfig.jmbgExtraction.location.fixedCell.column', value)}
                style={{ width: 80 }}
                size="small"
              >
                {COLUMN_OPTIONS.slice(0, 10).map((col) => (
                  <Option key={col} value={col}>{col}</Option>
                ))}
              </Select>
            </Space>
          </Col>
        </Row>
      </div>
    );
  };

  const renderUploadStep = () => (
    <div style={{ maxWidth: 700, margin: '0 auto' }}>
      <Alert
        message="Upload Email File"
        description="Upload an email file (.eml or .msg) containing Excel timesheets as attachments. The AI will analyze the Excel format and generate a parsing configuration."
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />

      <Dragger
        accept=".eml,.msg"
        maxCount={1}
        beforeUpload={() => false}
        onChange={handleFileUpload}
        fileList={uploadedFile ? [uploadedFile] : []}
      >
        <p className="ant-upload-drag-icon">
          <MailOutlined style={{ fontSize: 48, color: '#1890ff' }} />
        </p>
        <p className="ant-upload-text">Click or drag email file to this area</p>
        <p className="ant-upload-hint">
          Supports .eml and .msg files with Excel attachments (.xlsx, .xls, .xlsm)
        </p>
      </Dragger>

      {uploadedFile && !emlPreview && (
        <div style={{ marginTop: 24, textAlign: 'center' }}>
          <Button
            type="primary"
            size="large"
            icon={<FileExcelOutlined />}
            loading={previewMutation.isPending}
            onClick={handlePreview}
          >
            {previewMutation.isPending ? 'Extracting...' : 'Preview Attachments'}
          </Button>
        </div>
      )}

      {emlPreview && (
        <Card title="EML Preview" style={{ marginTop: 24 }}>
          <Descriptions column={2} size="small">
            <Descriptions.Item label="From">{emlPreview.metadata?.from}</Descriptions.Item>
            <Descriptions.Item label="To">{emlPreview.metadata?.to}</Descriptions.Item>
            <Descriptions.Item label="Subject" span={2}>{emlPreview.metadata?.subject}</Descriptions.Item>
            <Descriptions.Item label="Date">{new Date(emlPreview.metadata?.date).toLocaleString()}</Descriptions.Item>
            <Descriptions.Item label="Attachments">{emlPreview.attachmentCount}</Descriptions.Item>
          </Descriptions>

          <Divider orientation="left">Excel Attachments</Divider>

          <List
            size="small"
            dataSource={emlPreview.attachments || []}
            renderItem={(attachment) => (
              <List.Item
                actions={[
                  <Button
                    key="preview"
                    type="link"
                    size="small"
                    icon={<EyeOutlined />}
                    onClick={() => setSelectedPreviewSheet(
                      selectedPreviewSheet === attachment.filename ? null : attachment.filename
                    )}
                  >
                    {selectedPreviewSheet === attachment.filename ? 'Hide' : 'Preview'}
                  </Button>,
                ]}
              >
                <List.Item.Meta
                  avatar={attachment.fromZip ? <FileZipOutlined /> : <FileExcelOutlined style={{ color: '#52c41a' }} />}
                  title={attachment.filename}
                  description={
                    <Space>
                      <Tag>{attachment.format?.toUpperCase()}</Tag>
                      <Text type="secondary">{(attachment.size / 1024).toFixed(1)} KB</Text>
                      <Text type="secondary">{attachment.sheetCount} sheet(s)</Text>
                      {attachment.fromZip && <Tag color="blue">From ZIP</Tag>}
                    </Space>
                  }
                />
              </List.Item>
            )}
          />

          {/* Excel Preview Table */}
          {selectedPreviewSheet && (
            <Card
              size="small"
              style={{ marginTop: 16 }}
              title={
                <Space>
                  <TableOutlined />
                  <span>Excel Preview: {selectedPreviewSheet}</span>
                </Space>
              }
            >
              {renderExcelPreviewTable(
                emlPreview.attachments?.find((a) => a.filename === selectedPreviewSheet)
              )}
            </Card>
          )}

          <div style={{ marginTop: 24, textAlign: 'center' }}>
            <Button
              type="primary"
              onClick={() => setCurrentStep(1)}
            >
              Continue to Analysis
            </Button>
          </div>
        </Card>
      )}
    </div>
  );

  const renderAnalyzeStep = () => (
    <div style={{ maxWidth: 700, margin: '0 auto' }}>
      <Card title="Configuration Setup">
        <Form form={form} layout="vertical">
          <Form.Item
            name="configName"
            label="Configuration Name"
            rules={[{ required: true, message: 'Configuration name is required' }]}
          >
            <Input placeholder="e.g., PCCC Metro Timesheet Parser" />
          </Form.Item>

          <Form.Item name="companyId" label="Assign to Company (Optional)">
            <Select
              placeholder="Select company"
              allowClear
              showSearch
              optionFilterProp="children"
            >
              {companies.map((company) => (
                <Option key={company._id} value={company._id}>
                  {company.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>

        <Divider />

        <div style={{ textAlign: 'center' }}>
          <Space direction="vertical" size="large">
            <RobotOutlined style={{ fontSize: 48, color: '#722ed1' }} />
            <Title level={4}>Ready for AI Analysis</Title>
            <Paragraph type="secondary">
              The AI will analyze the Excel attachments and detect:
            </Paragraph>
            <ul style={{ textAlign: 'left', display: 'inline-block' }}>
              <li>Format type (column-based, text markers, symbols)</li>
              <li>Header row and data row positions</li>
              <li>Column mappings for hours, overtime, leave types</li>
              <li>Text markers for leave types (e.g., &quot;Annual Leave&quot;)</li>
            </ul>

            <Space>
              <Button onClick={() => setCurrentStep(0)}>
                Back
              </Button>
              <Button
                type="primary"
                size="large"
                icon={<RobotOutlined />}
                loading={analyzeMutation.isPending}
                onClick={handleAnalyze}
              >
                {analyzeMutation.isPending ? 'Analyzing...' : 'Analyze with AI'}
              </Button>
            </Space>
          </Space>
        </div>

        {analyzeMutation.isPending && (
          <div style={{ marginTop: 24, textAlign: 'center' }}>
            <Spin size="large" />
            <div style={{ marginTop: 16 }}>
              <Text type="secondary">AI is analyzing the Excel structure...</Text>
            </div>
            <Paragraph type="secondary" style={{ fontSize: 12 }}>
              This may take a few seconds. Please wait.
            </Paragraph>
          </div>
        )}
      </Card>
    </div>
  );

  const renderReviewStep = () => (
    <div>
      {analysisResult && (
        <>
          <Alert
            message="AI Analysis Complete"
            description={
              <Space>
                <span>Confidence Level:</span>
                <Tag color={analysisResult.analysisDetails?.confidence >= 80 ? 'green' : 'orange'}>
                  {analysisResult.analysisDetails?.confidence?.toFixed(0) || 80}%
                </Tag>
                <span>Format Type:</span>
                <Tag color="blue">
                  {analysisResult.analysisDetails?.formatType || 'column_based'}
                </Tag>
              </Space>
            }
            type="success"
            showIcon
            style={{ marginBottom: 24 }}
          />

          {analysisResult.analysisDetails?.reasoning && (
            <Alert
              message="AI Notes"
              description={analysisResult.analysisDetails.reasoning}
              type="info"
              showIcon
              style={{ marginBottom: 24 }}
            />
          )}

          <Form form={form} layout="vertical" onFinish={handleSaveConfig}>
            <Row gutter={24}>
              <Col span={12}>
                <Card title="Basic Info" size="small">
                  <Form.Item
                    name="name"
                    label="Configuration Name"
                    rules={[{ required: true, message: t('required', { ns: 'forms' }) }]}
                  >
                    <Input placeholder="e.g., METRO Timesheet Parser" />
                  </Form.Item>

                  <Form.Item name="description" label="Description">
                    <Input.TextArea rows={2} placeholder="Description of this parser configuration" />
                  </Form.Item>

                  <Form.Item name="companyId" label="Assign to Company">
                    <Select
                      placeholder="Select company"
                      allowClear
                      showSearch
                      optionFilterProp="children"
                    >
                      {companies.map((company) => (
                        <Option key={company._id} value={company._id}>
                          {company.name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>

                  <div style={{ marginTop: 16, padding: 12, background: '#f5f5f5', borderRadius: 6 }}>
                    <Space align="start">
                      <Checkbox
                        checked={multipleFormats}
                        onChange={handleMultipleFormatsChange}
                      >
                        <Text strong>{sp('multipleFormats')}</Text>
                      </Checkbox>
                      <Tooltip title={sp('multipleFormatsTooltip')}>
                        <QuestionCircleOutlined style={{ color: '#1890ff', cursor: 'help' }} />
                      </Tooltip>
                    </Space>
                  </div>
                </Card>
              </Col>

              <Col span={12}>
                <Card title="Detected Structure (Read-Only Summary)" size="small">
                  <Descriptions column={1} size="small">
                    <Descriptions.Item label="Header Row">
                      {editedConfig?.excelConfig?.headerDetection?.fixedRow || 5}
                    </Descriptions.Item>
                    <Descriptions.Item label="Data Rows">
                      {editedConfig?.excelConfig?.dataRows?.fixedCount || 31} days
                    </Descriptions.Item>
                    <Descriptions.Item label="Text Markers">
                      {editedConfig?.textMarkerConfig?.enabled ? 'Enabled' : 'Disabled'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Symbol Markers">
                      {editedConfig?.symbolMarkerConfig?.enabled ? 'Enabled' : 'Disabled'}
                    </Descriptions.Item>
                    <Descriptions.Item label="JMBG Extraction">
                      {editedConfig?.excelConfig?.jmbgExtraction?.enabled ? 'Enabled' : 'Disabled'}
                    </Descriptions.Item>
                  </Descriptions>
                </Card>
              </Col>
            </Row>

            <Divider />

            {/* Multiple Formats Section */}
            {multipleFormats && (
              <>
                <Card
                  title={
                    <Space>
                      <FileExcelOutlined />
                      <span>Format Mappings</span>
                      <Tag color="blue">{formatMappings.length} format(s)</Tag>
                    </Space>
                  }
                  size="small"
                  extra={
                    <Button
                      type="primary"
                      size="small"
                      icon={<PlusOutlined />}
                      onClick={handleAddFormatMapping}
                    >
                      {sp('addFormatMapping')}
                    </Button>
                  }
                >
                  {formatMappings.length === 0 ? (
                    <Alert
                      message="No format mappings defined"
                      description="Add format mappings to handle different Excel structures in your emails."
                      type="info"
                      showIcon
                    />
                  ) : (
                    <Collapse accordion>
                      {formatMappings.map((format, index) => (
                        <Collapse.Panel
                          key={format.id}
                          header={
                            <Space>
                              <Text strong>{format.name}</Text>
                              {format.isDefault && <Tag color="green">{sp('defaultFormat')}</Tag>}
                              {format.detection?.patterns?.length > 0 && (
                                <Text type="secondary" style={{ fontSize: 11 }}>
                                  Patterns: {format.detection.patterns.join(', ')}
                                </Text>
                              )}
                            </Space>
                          }
                          extra={
                            <Space onClick={(e) => e.stopPropagation()}>
                              <Button
                                size="small"
                                icon={<EditOutlined />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditFormatMapping(index);
                                }}
                              />
                              {!format.isDefault && (
                                <Button
                                  size="small"
                                  danger
                                  icon={<DeleteOutlined />}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteFormatMapping(index);
                                  }}
                                />
                              )}
                            </Space>
                          }
                        >
                          <Tabs
                            size="small"
                            items={[
                              {
                                key: 'structure',
                                label: 'Structure Settings',
                                children: (
                                  <div style={{ padding: '8px 0' }}>
                                    <Row gutter={16}>
                                      <Col span={6}>
                                        <div style={{ marginBottom: 4 }}>
                                          <Text strong style={{ fontSize: 12 }}>{sp('headerRowLabel')}</Text>
                                          <Text type="secondary" style={{ fontSize: 10, display: 'block' }}>
                                            {sp('headerRowDesc')}
                                          </Text>
                                        </div>
                                        <InputNumber
                                          min={1}
                                          max={50}
                                          value={format.excelConfig?.headerDetection?.fixedRow || 4}
                                          onChange={(value) => handleFormatExcelConfigChange(index, 'headerDetection.fixedRow', value)}
                                          style={{ width: '100%' }}
                                          size="small"
                                        />
                                      </Col>
                                      <Col span={6}>
                                        <div style={{ marginBottom: 4 }}>
                                          <Text strong style={{ fontSize: 12 }}>{sp('dataStartOffset')}</Text>
                                          <Text type="secondary" style={{ fontSize: 10, display: 'block' }}>
                                            {sp('dataStartOffsetDesc')}
                                          </Text>
                                        </div>
                                        <InputNumber
                                          min={1}
                                          max={10}
                                          value={format.excelConfig?.dataRows?.startOffset || 1}
                                          onChange={(value) => handleFormatExcelConfigChange(index, 'dataRows.startOffset', value)}
                                          style={{ width: '100%' }}
                                          size="small"
                                        />
                                      </Col>
                                      <Col span={6}>
                                        <div style={{ marginBottom: 4 }}>
                                          <Text strong style={{ fontSize: 12 }}>{sp('dataRowCount')}</Text>
                                          <Text type="secondary" style={{ fontSize: 10, display: 'block' }}>
                                            {sp('dataRowCountDesc')}
                                          </Text>
                                        </div>
                                        <InputNumber
                                          min={1}
                                          max={50}
                                          value={format.excelConfig?.dataRows?.fixedCount || 31}
                                          onChange={(value) => handleFormatExcelConfigChange(index, 'dataRows.fixedCount', value)}
                                          style={{ width: '100%' }}
                                          size="small"
                                        />
                                      </Col>
                                      <Col span={6}>
                                        <div style={{ marginBottom: 4 }}>
                                          <Text strong style={{ fontSize: 12 }}>Skip Sheets</Text>
                                          <Text type="secondary" style={{ fontSize: 10, display: 'block' }}>
                                            Sheets to ignore (comma-sep)
                                          </Text>
                                        </div>
                                        <Input
                                          placeholder="source, employees"
                                          value={format.excelConfig?.skipSheets?.join(', ') || ''}
                                          onChange={(e) => handleFormatSkipSheetsChange(index, e.target.value)}
                                          size="small"
                                        />
                                      </Col>
                                    </Row>
                                    <Row gutter={16} style={{ marginTop: 12 }}>
                                      <Col span={12}>
                                        <div style={{ marginBottom: 4 }}>
                                          <Text strong style={{ fontSize: 12 }}>{sp('employeeNameLocation')}</Text>
                                        </div>
                                        <Space>
                                          <Select
                                            value={format.excelConfig?.employeeNameSource?.type || 'sheet_name'}
                                            onChange={(value) => handleFormatExcelConfigChange(index, 'employeeNameSource.type', value)}
                                            style={{ width: 140 }}
                                            size="small"
                                          >
                                            <Option value="sheet_name">Sheet Name</Option>
                                            <Option value="fixed_cell">Fixed Cell</Option>
                                          </Select>
                                          {format.excelConfig?.employeeNameSource?.type === 'fixed_cell' && (
                                            <>
                                              <Text type="secondary">{sp('row')}:</Text>
                                              <InputNumber
                                                min={1}
                                                max={50}
                                                value={format.excelConfig?.employeeNameSource?.fixedCell?.row || 37}
                                                onChange={(value) => handleFormatExcelConfigChange(index, 'employeeNameSource.fixedCell.row', value)}
                                                style={{ width: 60 }}
                                                size="small"
                                              />
                                              <Text type="secondary">{sp('column')}:</Text>
                                              <Select
                                                value={format.excelConfig?.employeeNameSource?.fixedCell?.column || 'A'}
                                                onChange={(value) => handleFormatExcelConfigChange(index, 'employeeNameSource.fixedCell.column', value)}
                                                style={{ width: 60 }}
                                                size="small"
                                              >
                                                {COLUMN_OPTIONS.slice(0, 10).map((col) => (
                                                  <Option key={col} value={col}>{col}</Option>
                                                ))}
                                              </Select>
                                            </>
                                          )}
                                        </Space>
                                      </Col>
                                      <Col span={12}>
                                        <div style={{ marginBottom: 4 }}>
                                          <Text strong style={{ fontSize: 12 }}>JMBG Extraction</Text>
                                        </div>
                                        <Space>
                                          <Checkbox
                                            checked={format.excelConfig?.jmbgExtraction?.enabled !== false}
                                            onChange={(e) => handleFormatExcelConfigChange(index, 'jmbgExtraction.enabled', e.target.checked)}
                                          >
                                            Enabled
                                          </Checkbox>
                                          {format.excelConfig?.jmbgExtraction?.enabled !== false && (
                                            <>
                                              <Text type="secondary">{sp('row')}:</Text>
                                              <InputNumber
                                                min={1}
                                                max={50}
                                                value={format.excelConfig?.jmbgExtraction?.location?.fixedCell?.row || 3}
                                                onChange={(value) => handleFormatExcelConfigChange(index, 'jmbgExtraction.location.fixedCell.row', value)}
                                                style={{ width: 60 }}
                                                size="small"
                                              />
                                              <Text type="secondary">{sp('column')}:</Text>
                                              <Select
                                                value={format.excelConfig?.jmbgExtraction?.location?.fixedCell?.column || 'C'}
                                                onChange={(value) => handleFormatExcelConfigChange(index, 'jmbgExtraction.location.fixedCell.column', value)}
                                                style={{ width: 60 }}
                                                size="small"
                                              >
                                                {COLUMN_OPTIONS.slice(0, 10).map((col) => (
                                                  <Option key={col} value={col}>{col}</Option>
                                                ))}
                                              </Select>
                                            </>
                                          )}
                                        </Space>
                                      </Col>
                                    </Row>
                                  </div>
                                ),
                              },
                              {
                                key: 'columns',
                                label: 'Column Mapping',
                                children: (
                                  <div style={{ padding: '8px 0' }}>
                                    <Row gutter={[16, 12]}>
                                      {COLUMN_MAPPING_FIELDS.map((field) => (
                                        <Col span={8} key={field.key}>
                                          <div style={{ marginBottom: 4 }}>
                                            <Text style={{ fontSize: 12 }}>{field.label}</Text>
                                          </div>
                                          <Select
                                            placeholder="Column"
                                            value={format.columnMapping?.[field.key]?.columnLetter || undefined}
                                            onChange={(value) => handleFormatColumnMappingChange(index, field.key, value)}
                                            allowClear
                                            style={{ width: '100%' }}
                                            size="small"
                                          >
                                            {COLUMN_OPTIONS.map((col) => (
                                              <Option key={col} value={col}>
                                                {col}
                                              </Option>
                                            ))}
                                          </Select>
                                        </Col>
                                      ))}
                                    </Row>
                                  </div>
                                ),
                              },
                            ]}
                          />
                        </Collapse.Panel>
                      ))}
                    </Collapse>
                  )}
                </Card>
                <Divider />
              </>
            )}

            {/* Tabbed Editor Section - shown only when NOT using multiple formats */}
            {!multipleFormats && (
            <Card
              title={
                <Space>
                  <EditOutlined />
                  <span>Configuration Editor</span>
                </Space>
              }
              size="small"
            >
              <Tabs
                defaultActiveKey="excel-preview"
                items={[
                  {
                    key: 'excel-preview',
                    label: (
                      <span>
                        <TableOutlined /> Excel Preview
                      </span>
                    ),
                    children: (
                      <div>
                        {emlPreview?.attachments?.length > 0 ? (
                          <div>
                            <Select
                              placeholder="Select attachment to preview"
                              style={{ width: 300, marginBottom: 16 }}
                              onChange={setSelectedPreviewSheet}
                              value={selectedPreviewSheet}
                            >
                              {emlPreview.attachments.map((att) => (
                                <Option key={att.filename} value={att.filename}>
                                  {att.filename}
                                </Option>
                              ))}
                            </Select>
                            {selectedPreviewSheet && renderExcelPreviewTable(
                              emlPreview.attachments.find((a) => a.filename === selectedPreviewSheet)
                            )}
                          </div>
                        ) : (
                          <Text type="secondary">No Excel attachments available for preview</Text>
                        )}
                      </div>
                    ),
                  },
                  {
                    key: 'column-mapping',
                    label: (
                      <span>
                        <EditOutlined /> Column Mapping
                      </span>
                    ),
                    children: renderColumnMappingEditor(),
                  },
                  {
                    key: 'structure',
                    label: (
                      <span>
                        <FileExcelOutlined /> Structure Settings
                      </span>
                    ),
                    children: renderStructureEditor(),
                  },
                  {
                    key: 'text-markers',
                    label: 'Text Markers',
                    children: (
                      <Descriptions column={1} size="small">
                        {editedConfig?.textMarkerConfig?.markers && Object.entries(editedConfig.textMarkerConfig.markers)
                          .filter(([_, markers]) => markers?.length > 0)
                          .map(([type, markers]) => (
                            <Descriptions.Item key={type} label={type}>
                              {markers.map((m, i) => <Tag key={i}>{m}</Tag>)}
                            </Descriptions.Item>
                          ))}
                        {(!editedConfig?.textMarkerConfig?.markers ||
                          Object.values(editedConfig.textMarkerConfig.markers).every((m) => !m?.length)) && (
                          <Text type="secondary">No text markers configured</Text>
                        )}
                      </Descriptions>
                    ),
                  },
                  {
                    key: 'eml-info',
                    label: 'EML Info',
                    children: (
                      <Descriptions column={2} size="small">
                        <Descriptions.Item label="From">{analysisResult.emlMetadata?.from}</Descriptions.Item>
                        <Descriptions.Item label="Subject">{analysisResult.emlMetadata?.subject}</Descriptions.Item>
                        <Descriptions.Item label="Attachments">{analysisResult.attachmentCount}</Descriptions.Item>
                        <Descriptions.Item label="Analyzed File">
                          {analysisResult.analysisDetails?.analyzedFile}
                        </Descriptions.Item>
                      </Descriptions>
                    ),
                  },
                ]}
              />
            </Card>
            )}

            <Divider />

            <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
              <Space>
                <Button icon={<ReloadOutlined />} onClick={handleReset}>
                  Start Over
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SaveOutlined />}
                  loading={createConfigMutation.isPending}
                >
                  Save Configuration
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </>
      )}
    </div>
  );

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Title level={4} style={{ margin: 0 }}>
          <RobotOutlined /> Format Analyzer (Learn Mode)
        </Title>
        <Text type="secondary">
          Upload an EML file with Excel attachments. AI will analyze the format and generate a parser configuration.
        </Text>
      </div>

      <Steps
        current={currentStep}
        items={steps}
        style={{ marginBottom: 32 }}
      />

      <Card>
        {currentStep === 0 && renderUploadStep()}
        {currentStep === 1 && renderAnalyzeStep()}
        {currentStep === 2 && renderReviewStep()}
      </Card>

      {/* Format Mapping Modal */}
      <Modal
        title={editingFormatIndex !== null ? 'Edit Format Mapping' : sp('addFormatMapping')}
        open={formatModalVisible}
        onCancel={() => {
          setFormatModalVisible(false);
          formatForm.resetFields();
        }}
        footer={null}
        width={500}
      >
        <Form
          form={formatForm}
          layout="vertical"
          onFinish={handleSaveFormatMapping}
        >
          <Form.Item
            name="name"
            label={sp('formatMappingName')}
            rules={[{ required: true, message: 'Format name is required' }]}
          >
            <Input placeholder="e.g., Foreign Employees, Metro L1P1, BBP Daily" />
          </Form.Item>

          <Form.Item
            name="patterns"
            label={
              <Space>
                <span>{sp('formatMappingPattern')}</span>
                <Tooltip title={sp('formatMappingPatternTooltip')}>
                  <QuestionCircleOutlined style={{ color: '#1890ff' }} />
                </Tooltip>
              </Space>
            }
          >
            <Input placeholder="foreign, metro, l1p1 (comma separated)" />
          </Form.Item>

          <Form.Item
            name="isDefault"
            valuePropName="checked"
          >
            <Checkbox>
              <Space>
                <span>{sp('defaultFormat')}</span>
                <Tooltip title={sp('defaultFormatTooltip')}>
                  <QuestionCircleOutlined style={{ color: '#1890ff' }} />
                </Tooltip>
              </Space>
            </Checkbox>
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setFormatModalVisible(false)}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                {editingFormatIndex !== null ? 'Update' : 'Add'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
